import React, { FC, memo, useCallback, useEffect, useState } from 'react';

import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import usePrevious from '@smart-tv/hooks/use-previous';
import { LiveTvChannel } from '@smart-tv/typings/content';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import clsx from 'clsx';

import {
  CHANNELS_FOCUS_KEY,
  CHANNELS_SELECTED_FOCUS_KEY,
  SELECTED_ITEM_FOCUS_KEY
} from './favorites-edit-helpers';
import { FavoritesEditSelectedChannelsOrderButtonValue } from './favorites-edit-selected-channels-row-order-buttons';
import styles from '../favorites.module.scss';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { SelectedChannelRow } from './favorites-edit-selected-channels-row';

const SCROLL_HEIGHT = 651;
const LIST_ITEM_HEIGHT = 93;

export const FavoritesEditSelectedChannels: FC = () => {
  const {
    channels,
    editedFavoritesList,
    handleChannelToggle,
    updateSids,
    setHasChanges,
    lastToggledChannel
  } = useFavoritesEdit();

  const [focusedButtonSid, setFocusedButtonSid] = useState<number>();
  const [selectedChannels, setSelectedChannels] = useState<LiveTvChannel[]>([]);
  const [lastFocusedSid, setLastFocusedSid] = useState<number>();
  const [isDragging, setIsDragging] = useState(false);

  const prevSelectedChannels = usePrevious(selectedChannels);

  const { ref: scrollingRef, focusKey } = useFocusable({
    onBlur: () => {
      setLastFocusedSid(focusedButtonSid);
      setFocusedButtonSid(undefined);
    },
    focusKey: CHANNELS_SELECTED_FOCUS_KEY,
    focusable: editedFavoritesList?.sids.length !== 0,
    preferredChildFocusKey: SELECTED_ITEM_FOCUS_KEY + lastFocusedSid,
    autoRestoreFocus: false
  });

  useEffect(() => {
    if (!channels) return;
    const newSelectedChannels = editedFavoritesList?.sids
      .map((sid) => channels.find((channel) => channel.sid === sid))
      .filter((channel) => channel);

    setSelectedChannels(newSelectedChannels as LiveTvChannel[]);

    if (newSelectedChannels?.length === 0) {
      setLastFocusedSid(undefined);
      setFocusedButtonSid(undefined);
      setFocus(CHANNELS_FOCUS_KEY);
    }
  }, [channels, editedFavoritesList?.sids, scrollingRef]);

  useEffect(() => {
    if (
      prevSelectedChannels &&
      prevSelectedChannels.length > 0 &&
      selectedChannels &&
      selectedChannels.length > prevSelectedChannels.length &&
      selectedChannels.length !== channels?.length &&
      scrollingRef.current !== null
    ) {
      scrollingRef.current.scrollTo({
        top:
          scrollingRef.current.getBoundingClientRect().top +
          LIST_ITEM_HEIGHT * selectedChannels.length,
        behavior: 'auto'
      });
    }
  }, [channels?.length, prevSelectedChannels, scrollingRef, selectedChannels]);

  useEffect(() => {
    if (
      lastToggledChannel?.wasAdded !== false ||
      !prevSelectedChannels ||
      selectedChannels.length === 0 ||
      selectedChannels.some((channel) => channel.sid === lastToggledChannel.sid)
    ) {
      return;
    }

    const channelIndex = prevSelectedChannels.findIndex(
      (channel) => channel.sid === lastToggledChannel.sid
    );
    if (channelIndex === -1) return;

    const nextFocusIndex = Math.min(channelIndex, selectedChannels.length - 1);
    setFocus(SELECTED_ITEM_FOCUS_KEY + selectedChannels[nextFocusIndex].sid);
  }, [lastToggledChannel, prevSelectedChannels, selectedChannels]);

  const channelFocused = useCallback(
    ({ top }: { top: number }, sid: number) => {
      if (scrollingRef.current !== null) {
        scrollingRef.current.scrollTo({
          top: top - SCROLL_HEIGHT,
          behavior: 'auto'
        });
      }
      setFocusedButtonSid(sid);
    },
    [scrollingRef]
  );

  const updateOrder = useCallback(
    (startIndex: number, change: number) => {
      if (!editedFavoritesList) return;

      const newIndex = startIndex + change;
      if (newIndex < 0 || newIndex >= selectedChannels.length) return;

      const orderedSelectedChannels = [...selectedChannels];
      const element = orderedSelectedChannels.splice(startIndex, 1)[0];
      orderedSelectedChannels.splice(newIndex, 0, element);
      const newSids = orderedSelectedChannels.map((channel) => channel.sid);

      updateSids(() => newSids);
      setHasChanges(true);
    },
    [editedFavoritesList, selectedChannels, updateSids, setHasChanges]
  );

  const moveItem = useCallback(
    (index: number, direction: FavoritesEditSelectedChannelsOrderButtonValue) => {
      if (
        (index === 0 && direction === FavoritesEditSelectedChannelsOrderButtonValue.Up) ||
        (index === selectedChannels.length - 1 &&
          direction === FavoritesEditSelectedChannelsOrderButtonValue.Down)
      ) {
        return;
      }

      const changeInIndex = direction === FavoritesEditSelectedChannelsOrderButtonValue.Up ? -1 : 1;
      updateOrder(index, changeInIndex);

      if (scrollingRef.current !== null) {
        let scrollTop = scrollingRef.current.getBoundingClientRect().top;
        if (direction === FavoritesEditSelectedChannelsOrderButtonValue.Up) {
          scrollTop = scrollTop + LIST_ITEM_HEIGHT * (index - 1);
        } else if (direction === FavoritesEditSelectedChannelsOrderButtonValue.Down) {
          scrollTop = scrollTop + LIST_ITEM_HEIGHT * (index + 1);
        }

        scrollingRef.current.scrollTo({
          top: scrollTop - SCROLL_HEIGHT,
          behavior: 'auto'
        });
      }
    },
    [updateOrder, selectedChannels.length, scrollingRef]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      setIsDragging(false);
      if (!result.destination || result.destination.index === result.source.index) return;
      updateOrder(result.source.index, result.destination.index - result.source.index);
    },
    [updateOrder]
  );

  if (selectedChannels.length === 0) {
    return (
      <div className={styles['favorites-edit-root--empty']}>
        <Typography className={styles['favorites-edit-root--empty-title']} variant="h6">
          {editedFavoritesList?.name}
        </Typography>
        <Typography
          className={styles['favorites-edit-root--empty-secondary-title']}
          variant="caption"
        >
          Du har ikke valgt nogen kanaler endnu
        </Typography>
        <Typography
          className={styles['favorites-edit-root--empty-secondary-title']}
          variant="caption"
        >
          Tryk på de kanaler du vil tilføje til listen
        </Typography>
      </div>
    );
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['favorites-edit-root--selected']}>
        <div className={styles['favorites-edit-root--selected-header']}>
          <Typography
            className={styles['favorites-edit-root--selected-title']}
            data-testid={testIds.favorites.edit.listTitle}
            variant="h6"
          >
            {editedFavoritesList?.name}
          </Typography>
          {IS_BROWSER && (
            <Typography
              className={styles['favorites-edit-root--selected-secondary-title']}
              variant="caption"
            >
              Træk i kanaler for at ændre rækkefølgen
            </Typography>
          )}
        </div>
        <div
          className={
            IS_BROWSER
              ? styles['favorites-edit-root--list-browser']
              : styles['favorites-edit-root--list-tv']
          }
          data-testid={testIds.favorites.edit.selectedChannels}
          ref={scrollingRef}
        >
          <DragDropContext onDragEnd={onDragEnd} onDragStart={() => setIsDragging(true)}>
            <Droppable droppableId="selected-channels" isDropDisabled={!IS_BROWSER}>
              {({ placeholder, innerRef, droppableProps }) => (
                <div
                  ref={innerRef}
                  {...droppableProps}
                  id="selected-channels-droppable"
                  className={clsx({
                    [styles['favorites-edit-root--draggable']]: IS_BROWSER,
                    [styles['favorites-edit-root--dragging']]: IS_BROWSER && isDragging
                  })}
                >
                  {selectedChannels?.map((channel, index) => (
                    <Draggable
                      key={channel.sid}
                      draggableId={String(channel.sid)}
                      index={index}
                      isDragDisabled={!IS_BROWSER}
                    >
                      {(draggableProvided) => (
                        <SelectedChannelRow
                          channel={channel}
                          index={index}
                          draggableProvided={draggableProvided}
                          focusedButtonSid={focusedButtonSid}
                          isLast={index === selectedChannels.length - 1}
                          onPointerLeave={() => setFocusedButtonSid(undefined)}
                          onPointerEnter={() => setFocusedButtonSid(channel.sid)}
                          onFocus={(layout) => channelFocused(layout, channel.sid)}
                          onToggle={() => handleChannelToggle(channel.sid)}
                          onMove={(direction) => moveItem(index, direction)}
                        />
                      )}
                    </Draggable>
                  ))}
                  {placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default memo(FavoritesEditSelectedChannels);
